import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";

export function Modal({
  onClose,
  children,
  maxWidth = "28rem",
  panelStyle,
  closeOnBackdrop = true,
}: {
  onClose: () => void;
  children: ReactNode;
  maxWidth?: string;
  panelStyle?: CSSProperties;
  closeOnBackdrop?: boolean;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const pressStartedOnOverlay = useRef(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  function handleMouseDown(e: React.MouseEvent<HTMLDivElement>) {
    pressStartedOnOverlay.current = e.target === overlayRef.current;
  }
  function handleMouseUp(e: React.MouseEvent<HTMLDivElement>) {
    if (!closeOnBackdrop) {
      pressStartedOnOverlay.current = false;
      return;
    }
    if (pressStartedOnOverlay.current && e.target === overlayRef.current) {
      onClose();
    }
    pressStartedOnOverlay.current = false;
  }

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center px-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
    >
      <div
        className="relative w-full rounded-3xl flex flex-col"
        style={{
          maxWidth,
          maxHeight: "calc(100vh - 2rem)",
          backgroundColor: "#ffffff",
          border: "1px solid rgba(0,0,0,0.10)",
          boxShadow: "0 32px 80px -24px rgba(0,0,0,0.25)",
          ...panelStyle,
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition-colors z-10"
          style={{ color: "#71717a", backgroundColor: "transparent" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "rgba(0,0,0,0.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="overflow-y-auto p-8 flex-1 min-h-0">{children}</div>
      </div>
    </div>
  );
}
